import React from "react"
import { spacing } from "@material-ui/system"
import {
  TextField as MuiTextField,
  Grid,
  IconButton,
  Dialog,
  DialogContent,
  DialogActions,
  DialogTitle,
  Button,
  CircularProgress,
  Tooltip,
} from "@material-ui/core"
import Autocomplete from "@material-ui/lab/Autocomplete"
import styled from "styled-components/macro"
import SearchIcon from "@material-ui/icons/Search"
import ClearIcon from "@material-ui/icons/Clear"
import MyLocationIcon from "@material-ui/icons/MyLocation"
import _ from "lodash"

const TextField = styled(MuiTextField)(spacing)

function loadGoogleMapsScript(callback) {
  if (window.google && window.google.maps) {
    callback()
    return
  }

  const existingScript = document.getElementById("google-maps-script")
  if (existingScript) {
    const interval = setInterval(() => {
      if (window.google && window.google.maps) {
        clearInterval(interval)
        callback()
      }
    }, 100)

    existingScript.addEventListener("load", () => {
      clearInterval(interval)
      callback()
    })
    return
  }

  //AIzaSyDXiDL_A6P7HK15MSTqVEXRKMbsmY4Y0G8
  const key =
    "AIzaSyDXiDL_A6P7HK15MSTqVEXRKMbsmY4Y0G8" ||
    process.env.REACT_APP_GOOGLE_MAPS_API_KEY
  const keyParam = key ? `key=${key}&` : ""
  const script = document.createElement("script")
  script.id = "google-maps-script"
  script.src = `https://maps.googleapis.com/maps/api/js?${keyParam}libraries=places`
  script.async = true
  script.defer = true
  script.onload = () => {
    if (callback) callback()
  }
  document.body.appendChild(script)
}

const COUNTRY_CONFIGS = {
  US: {
    countryCodes: "us",
    defaultCenter: [40.7128, -74.006], // NYC
    bounds: {
      southWest: [18.0, -180.0],
      northEast: [72.0, -60.0],
    },
  },
  IN: {
    countryCodes: "in",
    defaultCenter: [20.5937, 78.9629], // Center of India
    bounds: {
      southWest: [6.0, 68.0],
      northEast: [37.0, 97.0],
    },
  },
  AE: {
    countryCodes: "ae",
    defaultCenter: [24.4539, 54.3773], // Abu Dhabi
    bounds: {
      southWest: [22.0, 51.0],
      northEast: [26.5, 57.0],
    },
  },
}

const getCountryConfig = (countryCode) => {
  const code = (countryCode || "").toUpperCase()
  if (code === "US" || code === "USA") return COUNTRY_CONFIGS.US
  if (code === "IN" || code === "IND" || code === "INDIA")
    return COUNTRY_CONFIGS.IN
  if (code === "AE" || code === "ARE" || code === "UAE")
    return COUNTRY_CONFIGS.AE
  return COUNTRY_CONFIGS.US // default to USA
}

const parseAddressComponents = (addressComponents) => {
  let streetNumber = ""
  let route = ""
  let sublocality = ""
  let city = ""
  let state = ""
  let stateShort = ""
  let postalCode = ""

  console.log("addressComponents", addressComponents)

  if (Array.isArray(addressComponents)) {
    for (const component of addressComponents) {
      const types = component.types || []
      if (types.includes("street_number")) {
        streetNumber = component.long_name || ""
      }
      if (types.includes("route")) {
        route = component.long_name || ""
      }
      if (
        types.includes("sublocality_level_1") ||
        types.includes("sublocality") ||
        types.includes("neighborhood")
      ) {
        sublocality = component.long_name || ""
      }
      if (types.includes("locality") || types.includes("postal_town")) {
        city = component.long_name || ""
      }
      if (types.includes("administrative_area_level_2") && !city) {
        city = component.long_name || ""
      }
      if (types.includes("administrative_area_level_1")) {
        state = component.long_name || ""
        stateShort = component.short_name || ""
      }
      if (types.includes("postal_code")) {
        postalCode = component.long_name || ""
      }
    }
  }

  // Fallback for city if locality/admin_area_2 is not set but sublocality is
  if (!city && sublocality) {
    city = sublocality
  }

  const mainStreet = [streetNumber, route].filter(Boolean).join(" ")
  const line1Parts = [mainStreet, sublocality].filter(Boolean)
  const line1 = line1Parts.join(", ").trim() || "-"

  return {
    line1,
    state,
    stateShort,
    city,
    postalcode: postalCode,
  }
}

function GeoLocation(props = {}) {
  const [getOpen, setOpen] = React.useState(false)
  const [searchText, setSearchText] = React.useState("")
  const [suggestions, setSuggestions] = React.useState([])
  const [loadingSearch, setLoadingSearch] = React.useState(false)
  const [mapLoaded, setMapLoaded] = React.useState(false)

  const mapRef = React.useRef(null)
  const markerRef = React.useRef(null)
  const infoWindowRef = React.useRef(null)
  const autocompleteServiceRef = React.useRef(null)
  const geocoderRef = React.useRef(null)

  React.useEffect(() => {
    loadGoogleMapsScript(() => {
      setMapLoaded(true)
    })
  }, [])

  const reverseGeocode = async (lat, lng) => {
    if (!window.google || !window.google.maps) return
    if (!geocoderRef.current) {
      geocoderRef.current = new window.google.maps.Geocoder()
    }

    try {
      geocoderRef.current.geocode(
        { location: { lat, lng } },
        (results, status) => {
          if (status === "OK" && results && results[0]) {
            const result = results[0]
            console.log(`Address: ${result.formatted_address}`, result)

            const parsed = parseAddressComponents(result.address_components)

            let matchedState = parsed.state
            if (props?.getStatesList && props?.getStatesList.length > 0) {
              const found = props.getStatesList.find(
                (s) =>
                  s.name?.toLowerCase() === parsed.state?.toLowerCase() ||
                  s.state?.toLowerCase() === parsed.state?.toLowerCase() ||
                  s.name?.toLowerCase() === parsed.stateShort?.toLowerCase() ||
                  s.state?.toLowerCase() === parsed.stateShort?.toLowerCase()
              )
              if (found) {
                matchedState = found.name || found.state
              }
            }

            props?.setFieldValue("line1", parsed.line1)
            props?.setFieldValue("state", matchedState)
            props?.setFieldValue("city", parsed.city)
            props?.setFieldValue("postalcode", parsed.postalcode)

            // Show info window at the marker location with the address details
            if (markerRef.current && infoWindowRef.current && mapRef.current) {
              infoWindowRef.current.setContent(
                `<div style="color: #333; font-family: Inter, sans-serif; font-size: 12px; padding: 4px; line-height: 1.4;">
                <strong style="color: #023539;">Pinned Location</strong><br/>
                ${parsed.line1}<br/>
                ${parsed.city ? parsed.city + ", " : ""}${parsed.state} ${
                  parsed.postalcode
                }
              </div>`
              )
              infoWindowRef.current.open(mapRef.current, markerRef.current)
            }
          } else {
            console.log(`Address: Geocoding failed with status: ${status}`)
          }
        }
      )
    } catch (err) {
      console.log(`Address: Error fetching address`, err)
    }
  }

  const doTriggerMap = (lcLat, lcLog) => {
    if (!window.google || !window.google.maps) return

    // Get current country config
    const countryCode =
      props?.values?.addressCountryCode ||
      localStorage.getItem("country_code") ||
      "US"
    const config = getCountryConfig(countryCode)

    const boundsRestriction = {
      north: config.bounds.northEast[0],
      south: config.bounds.southWest[0],
      west: config.bounds.southWest[1],
      east: config.bounds.northEast[1],
    }

    const isWithinBounds = (lat, lng) => {
      return (
        lat >= config.bounds.southWest[0] &&
        lat <= config.bounds.northEast[0] &&
        lng >= config.bounds.southWest[1] &&
        lng <= config.bounds.northEast[1]
      )
    }

    const hasCoords = isWithinBounds(lcLat, lcLog) && lcLat !== 0 && lcLog !== 0
    const finalLat = hasCoords ? lcLat : config.defaultCenter[0]
    const finalLng = hasCoords ? lcLog : config.defaultCenter[1]
    const initialZoom = hasCoords
      ? 16
      : config.countryCodes === "us"
      ? 1
      : config.countryCodes === "in"
      ? 5
      : 7

    if (!hasCoords && props?.isView !== "create") {
      props?.setFieldValue("latitude", finalLat.toFixed(6))
      props?.setFieldValue("longitude", finalLng.toFixed(6))
    }

    if (mapRef.current) {
      updateMarkerAndCenter(finalLat, finalLng, 16)
      return
    }

    const mapElement = document.getElementById("map")
    if (!mapElement) return

    const map = new window.google.maps.Map(mapElement, {
      center: { lat: finalLat, lng: finalLng },
      zoom: initialZoom,
      restriction: {
        latLngBounds: boundsRestriction,
        strictBounds: true,
      },
      minZoom: config.countryCodes === "us" ? 3 : 5,
    })
    mapRef.current = map

    updateMarkerAndCenter(finalLat, finalLng, initialZoom)

    map.addListener("click", async (e) => {
      const clickLat = e.latLng.lat()
      const clickLng = e.latLng.lng()
      if (!isWithinBounds(clickLat, clickLng)) return

      props?.setFieldValue("latitude", clickLat.toFixed(6))
      props?.setFieldValue("longitude", clickLng.toFixed(6))
      props?.setFieldValue("line1", "-")

      updateMarkerAndCenter(clickLat, clickLng, 16)
      await reverseGeocode(clickLat, clickLng)
    })
  }

  const updateMarkerAndCenter = (lat, lng, zoomLevel = 16) => {
    if (!mapRef.current || !window.google || !window.google.maps) return

    mapRef.current.setCenter({ lat, lng })
    if (zoomLevel) {
      mapRef.current.setZoom(zoomLevel)
    }

    if (!infoWindowRef.current) {
      infoWindowRef.current = new window.google.maps.InfoWindow()
    }

    if (markerRef.current) {
      markerRef.current.setPosition({ lat, lng })
    } else {
      markerRef.current = new window.google.maps.Marker({
        position: { lat, lng },
        map: mapRef.current,
        draggable: true,
      })

      markerRef.current.addListener("dragstart", () => {
        if (infoWindowRef.current) {
          infoWindowRef.current.close()
        }
      })

      markerRef.current.addListener("dragend", async (e) => {
        const dragLat = e.latLng.lat()
        const dragLng = e.latLng.lng()
        props?.setFieldValue("latitude", dragLat.toFixed(6))
        props?.setFieldValue("longitude", dragLng.toFixed(6))
        props?.setFieldValue("line1", "-")

        updateMarkerAndCenter(dragLat, dragLng, 16)
        await reverseGeocode(dragLat, dragLng)
      })
    }

    // Force map container resize recalculation and re-center after delay
    setTimeout(() => {
      if (mapRef.current && window.google && window.google.maps) {
        window.google.maps.event.trigger(mapRef.current, "resize")
        mapRef.current.setCenter({ lat, lng })
      }
    }, 200)
  }

  React.useEffect(() => {
    if (!mapLoaded) return

    let lat = parseFloat(props?.values?.latitude)
    let lng = parseFloat(props?.values?.longitude)

    if (!isNaN(lat) && !isNaN(lng) && lat !== 0 && lng !== 0) {
      doTriggerMap(lat, lng)
    } else {
      navigator?.geolocation?.getCurrentPosition(
        (position) => {
          lat = position.coords.latitude
          lng = position.coords.longitude
          doTriggerMap(lat, lng)
          if (props?.isView === "create") {
            props?.setFieldValue("latitude", 0)
            props?.setFieldValue("longitude", 0)
          }
        },
        (error) => {
          // Default fallback location based on country config
          const countryCode =
            props?.values?.addressCountryCode ||
            localStorage.getItem("country_code") ||
            "US"
          const config = getCountryConfig(countryCode)
          doTriggerMap(config.defaultCenter[0], config.defaultCenter[1])
        }
      )
    }
  }, [mapLoaded])

  // Sync map center and marker position when lat/lng are edited directly in the inputs
  React.useEffect(() => {
    const lat = parseFloat(props?.values?.latitude)
    const lng = parseFloat(props?.values?.longitude)
    if (
      !isNaN(lat) &&
      !isNaN(lng) &&
      lat !== 0 &&
      lng !== 0 &&
      mapRef.current &&
      window.google &&
      window.google.maps
    ) {
      const currentCenter = mapRef.current.getCenter()
      const currentLat = currentCenter.lat()
      const currentLng = currentCenter.lng()
      if (
        Math.abs(currentLat - lat) > 0.0001 ||
        Math.abs(currentLng - lng) > 0.0001
      ) {
        updateMarkerAndCenter(lat, lng, 16)
      }
    }
  }, [props?.values?.latitude, props?.values?.longitude])

  // Dynamically update map maxBounds and center when Formik's country code changes
  React.useEffect(() => {
    const countryCode = props?.values?.addressCountryCode
    if (mapRef.current && countryCode && window.google && window.google.maps) {
      const config = getCountryConfig(countryCode)
      const boundsRestriction = {
        north: config.bounds.northEast[0],
        south: config.bounds.southWest[0],
        west: config.bounds.southWest[1],
        east: config.bounds.northEast[1],
      }

      mapRef.current.setOptions({
        restriction: {
          latLngBounds: boundsRestriction,
          strictBounds: true,
        },
        minZoom: config.countryCodes === "us" ? 3 : 5,
      })

      const center = mapRef.current.getCenter()
      const lat = center.lat()
      const lng = center.lng()

      const isWithinBounds =
        lat >= config.bounds.southWest[0] &&
        lat <= config.bounds.northEast[0] &&
        lng >= config.bounds.southWest[1] &&
        lng <= config.bounds.northEast[1]

      if (!isWithinBounds) {
        updateMarkerAndCenter(
          config.defaultCenter[0],
          config.defaultCenter[1],
          16
        )
        if (infoWindowRef.current) {
          infoWindowRef.current.close()
        }
      }
    }
  }, [props?.values?.addressCountryCode])

  const debouncedSearch = React.useCallback(
    _.debounce((query) => {
      if (!query.trim()) {
        setSuggestions([])
        return
      }
      if (!window.google || !window.google.maps || !window.google.maps.places) {
        setSuggestions([])
        return
      }
      if (!autocompleteServiceRef.current) {
        autocompleteServiceRef.current = new window.google.maps.places.AutocompleteService()
      }

      setLoadingSearch(true)

      const countryCode =
        props?.values?.addressCountryCode ||
        localStorage.getItem("country_code") ||
        "US"
      const config = getCountryConfig(countryCode)

      autocompleteServiceRef.current.getPlacePredictions(
        {
          input: query,
          componentRestrictions: { country: config.countryCodes },
        },
        (predictions, status) => {
          setLoadingSearch(false)
          if (
            status === window.google.maps.places.PlacesServiceStatus.OK &&
            predictions
          ) {
            setSuggestions(predictions)
          } else {
            setSuggestions([])
          }
        }
      )
    }, 500),
    [props?.values?.addressCountryCode]
  )

  React.useEffect(() => {
    return () => {
      debouncedSearch.cancel()
    }
  }, [debouncedSearch])

  const handleSelectSuggestion = (suggestion) => {
    if (!suggestion) return
    if (!window.google || !window.google.maps) return
    if (!geocoderRef.current) {
      geocoderRef.current = new window.google.maps.Geocoder()
    }

    setLoadingSearch(true)
    geocoderRef.current.geocode(
      { placeId: suggestion.place_id },
      (results, status) => {
        setLoadingSearch(false)
        if (status === "OK" && results && results[0]) {
          const result = results[0]
          const lat = result.geometry.location.lat()
          const lng = result.geometry.location.lng()

          props?.setFieldValue("latitude", lat.toFixed(6))
          props?.setFieldValue("longitude", lng.toFixed(6))

          const parsed = parseAddressComponents(result.address_components)

          let matchedState = parsed.state
          if (props?.getStatesList && props?.getStatesList.length > 0) {
            const found = props.getStatesList.find(
              (s) =>
                s.name?.toLowerCase() === parsed.state?.toLowerCase() ||
                s.state?.toLowerCase() === parsed.state?.toLowerCase() ||
                s.name?.toLowerCase() === parsed.stateShort?.toLowerCase() ||
                s.state?.toLowerCase() === parsed.stateShort?.toLowerCase()
            )
            if (found) {
              matchedState = found.name || found.state
            }
          }

          props?.setFieldValue("line1", parsed.line1)
          props?.setFieldValue("state", matchedState)
          props?.setFieldValue("city", parsed.city)
          props?.setFieldValue("postalcode", parsed.postalcode)

          updateMarkerAndCenter(lat, lng, 16)

          // Show info window at the marker location with the address details
          if (markerRef.current && infoWindowRef.current && mapRef.current) {
            infoWindowRef.current.setContent(
              `<div style="color: #333; font-family: Inter, sans-serif; font-size: 12px; padding: 4px; line-height: 1.4;">
                <strong style="color: #023539;">Pinned Location</strong><br/>
                ${parsed.line1}<br/>
                ${parsed.city ? parsed.city + ", " : ""}${parsed.state} ${
                parsed.postalcode
              }
              </div>`
            )
            infoWindowRef.current.open(mapRef.current, markerRef.current)
          }

          setSuggestions([])
        } else {
          console.error("Geocoding failed for place ID:", status)
        }
      }
    )
  }

  const handleCurrentLocation = (e) => {
    if (e) {
      e.stopPropagation()
      e.preventDefault()
    }
    if (navigator?.geolocation) {
      navigator.geolocation.getCurrentPosition(
        async (position) => {
          const lat = position.coords.latitude
          const lng = position.coords.longitude
          props?.setFieldValue("latitude", lat.toFixed(6))
          props?.setFieldValue("longitude", lng.toFixed(6))
          props?.setFieldValue("line1", "-")
          updateMarkerAndCenter(lat, lng, 16)
          await reverseGeocode(lat, lng)
        },
        (error) => {
          console.error("Error getting current location:", error)
        }
      )
    }
  }

  const renderSearch = () => {
    return (
      <div style={{ position: "relative", width: "100%", marginBottom: "8px" }}>
        <Autocomplete
          id="address-search-autocomplete"
          options={suggestions}
          getOptionLabel={(option) => option.description || ""}
          getOptionSelected={(option, value) =>
            option.place_id === value.place_id
          }
          loading={loadingSearch}
          inputValue={searchText}
          onInputChange={(event, newInputValue, reason) => {
            setSearchText(newInputValue)
            if (reason === "input") {
              debouncedSearch(newInputValue)
            }
          }}
          onChange={(event, newValue) => {
            if (newValue) {
              handleSelectSuggestion(newValue)
            }
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder={`Search ${props?.values?.addressCountryCode} Address`}
              variant="outlined"
              size="small"
              fullWidth
              InputProps={{
                ...params.InputProps,
                startAdornment: (
                  <>
                    <SearchIcon
                      fontSize="small"
                      style={{
                        color: "#757575",
                        marginRight: 8,
                        marginLeft: 4,
                      }}
                    />
                    {params.InputProps.startAdornment}
                  </>
                ),
                endAdornment: (
                  <React.Fragment>
                    {loadingSearch ? (
                      <CircularProgress color="inherit" size={20} />
                    ) : null}
                    <Tooltip title="Current Location">
                      <IconButton
                        size="small"
                        onClick={handleCurrentLocation}
                        style={{ padding: 4, marginRight: 4 }}
                      >
                        <MyLocationIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    {params.InputProps.endAdornment}
                  </React.Fragment>
                ),
              }}
            />
          )}
        />
      </div>
    )
  }

  return (
    <>
      <div
        style={{
          width: "100%",
          height: 340,
          maxWidth: 800,
          background: "#fff",
          borderRadius: 10,
          padding: 10,
          paddingLeft: 20,
          display: "flex",
          flexDirection: "column",
          position: "relative",
        }}
      >
        {renderSearch()}
        <div
          id="map"
          style={{
            width: "100%",
            height: 320,
            borderRadius: 10,
            overflow: "hidden",
            background: "#f0f0f0",
            ...(mapLoaded
              ? {}
              : {
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }),
          }}
        >
          {!mapLoaded && (
            <CircularProgress size={40} style={{ color: "#333" }} />
          )}
        </div>
      </div>

      <Grid
        item
        lg={12}
        md={12}
        sm={12}
        xs={12}
        style={{ display: "flex", alignItems: "center" }}
      >
        <TextField
          style={{ marginLeft: "20px" }}
          InputLabelProps={{ shrink: true }}
          type="number"
          defaultValue={props?.values.latitude}
          value={props?.values.latitude}
          variant="outlined"
          name="latitude"
          label="Latitude"
          placeholder="Latitude"
          onKeyPress={(e) => props?.handleKeyPress(e, "text")}
          error={Boolean(props?.touched.latitude && props?.errors.latitude)}
          helperText={props?.touched.latitude && props?.errors.latitude}
          onBlur={props?.handleBlur}
          className={props?.classes.textField}
          onChange={(innerProps) => {
            if (props?.values.latitude !== null) {
              props?.setAllowPrompt(true)
            }
            props?.handleChange(innerProps)
            props?.onGlobalUpdate()
          }}
          size="small"
        />
        <TextField
          InputLabelProps={{ shrink: true }}
          type="number"
          defaultValue={props?.values.longitude}
          value={props?.values.longitude}
          variant="outlined"
          name="longitude"
          label="Longitude"
          placeholder="Longitude"
          onKeyPress={(e) => props?.handleKeyPress(e, "text")}
          error={Boolean(props?.touched.longitude && props?.errors.longitude)}
          helperText={props?.touched.longitude && props?.errors.longitude}
          onBlur={props?.handleBlur}
          className={props?.classes.textField}
          onChange={(innerProps) => {
            if (props?.values.longitude !== null) {
              props?.setAllowPrompt(true)
            }
            props?.handleChange(innerProps)
            props?.onGlobalUpdate()
          }}
          size="small"
        />
      </Grid>
    </>
  )
}
export { GeoLocation }
export default GeoLocation
